import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { ACT_SLUG, DAT_SLUG, LSAT_SLUG, MCAT_SLUG, SAT_SLUG } from 'my-core/course-utils';
import { useCurrentUser } from 'my-core/hooks';
import { MCAT_SECTION_KEYS } from 'my-core/mcat-data';
import {
  aboutPath,
  accountSettingsPath,
  actEventPath,
  actEventsPath,
  actLandingPath,
  actTutoringLandingPath,
  adminDashboardPath,
  aiTutorPath,
  announcementsPath,
  blogPath,
  brandAmbassadorDashboardPath,
  callUsPath,
  careersPath,
  chatPagePath,
  checkoutPath,
  classRepDashboardPath,
  classRepPositionsPath,
  classRepWizardPath,
  contentCreatorDashboardPath,
  contestPath,
  courseAssignmentPath,
  courseBookletPath,
  courseClassRepApplyPath,
  coursePath,
  customProductPath,
  datEliteLandingPath,
  datEventPath,
  datEventsPath,
  datLandingPath,
  datSelfPacedLandingPath,
  datTutoringLandingPath,
  educatorsLandingPath,
  essayWriterLandingPagePath,
  essayWriterPath,
  flashCardsEditPath,
  flashCardsPath,
  highSchoolLandingPath,
  instructorDashboardPath,
  invitesPagePath,
  jobApplicantPath,
  livePrepLandingPath,
  livePrepSchoolPath,
  lsacPracticeTestPath,
  lsatEventPath,
  lsatEventsPath,
  lsatLandingPath,
  lsatSelfPacedLandingPath,
  lsatTutoringLandingPath,
  marketingRepDashboardPath,
  mcatDailyFlashCardsPath,
  mcatEliteLandingPath,
  mcatEventPath,
  mcatEventsPath,
  mcatExamDates,
  mcatExamReportPath,
  mcatFreeResourcesPath,
  mcatRepDashboardPath,
  mcatScorePath,
  mcatScoresPath,
  mcatSectionBalancePath,
  mcatSectionScorePath,
  medSchoolAdmissionsLandingPath,
  medSchoolCalculatorPath,
  onDemandLandingPath,
  parentsLandingPath,
  partnershipsPath,
  preMedPlusLandingPath,
  preMedTutoringLandingPath,
  pricingPath,
  satEventPath,
  satEventsPath,
  satLandingPath,
  satTutoringLandingPath,
  scoresmartTestResultsPath,
  searchPagePath,
  studentActDashboardPath,
  studentCoursePagePath,
  studentDashboardPath,
  studentDatDashboardPath,
  studentLsatDashboardPath,
  studentMcatDashboardPath,
  studentOnboardingPath,
  studentSatDashboardPath,
  studentSignupPath,
  studyInitiativePath,
  studyPlannerActivityPath,
  subscriptionLandingPath,
  teacherDashboardPath,
  textUsPath,
  tokensDashboardPath,
  tutoringDashboardPath,
  tutoringLandingPath,
  tutorSearchPath,
  universityLandingPath,
} from 'my-core/routes';
import { hasAnyRoles, hasManagerRole } from 'my-core/user-utils';

import ACTDashboard from 'my-components/ACTDashboard';
import ACTLandingPage from 'my-components/ACTLandingPage';
import ACTTutoringLandingPage from 'my-components/ACTTutoringLandingPage';
import AdmissionsLandingPage from 'my-components/AdmissionsLandingPage';
import Announcements from 'my-components/Announcements';
import BuilderLandingPage from 'my-components/BuilderLandingPage';
import ChatPage from 'my-components/ChatPage';
import ClassRepApplicationPage from 'my-components/ClassRepApplicationPage';
import ClassRepDashboard from 'my-components/ClassRepDashboard';
import ClassRepPositionsPage from 'my-components/ClassRepPositionsPage';
import ClassRepWizardPage from 'my-components/ClassRepWizardPage';
import ContestPage from 'my-components/ContestPage';
import CourseAssignmentPage from 'my-components/CourseAssignmentPage';
import CoursePage, { CourseBookletPage } from 'my-components/CoursePage';
import CustomProductCheckoutPage from 'my-components/CustomProductCheckoutPage';
import DATDashboard from 'my-components/DATDashboard';
import DATLandingPage, { DATEliteLandingPage, DATSelfPacedLandingPage } from 'my-components/DATLandingPage';
import DATTutoringLandingPage from 'my-components/DATTutoringLandingPage';
import DRMVideoTesting from 'my-components/DRMVideoTesting';
import EssayJack, { LandingPage as EssayJackLandingPage } from 'my-components/EssayJack';
import { FlashCardDeckEditPage } from 'my-components/FlashCardsManager';
import FlashCardsPage, { MCATDailyFlashCardsPage } from 'my-components/FlashCardsPage';
import FreeEventsLandingPage, { FreeEventLandingPage } from 'my-components/FreeEventsLandingPage';
import InstructorDashboard from 'my-components/InstructorDashboard';
import JobApplication from 'my-components/JobApplication';
import LlmChatManager from 'my-components/LlmChatManager';
import Loadable from 'my-components/Loadable';
import { LoginFormDialog, ReauthenticateForm } from 'my-components/LoginForm';
import LSACPracticeTestPage from 'my-components/LSACPracticeTestPage';
import LSATDashboard from 'my-components/LSATDashboard';
import LSATLandingPage, { LSATSelfPacedLandingPage } from 'my-components/LSATLandingPage';
import LSATTutoringLandingPage from 'my-components/LSATTutoringLandingPage';
import MarketingRepDashboard from 'my-components/MarketingRepDashboard';
import MCATCARSPassagePage from 'my-components/MCATCARSPassagePage';
import MCATDashboard from 'my-components/MCATDashboard';
import MCATExamDates from 'my-components/MCATExamDates';
import MCATExamReportPage from 'my-components/MCATExamReportPage';
import { MCAT, MCATFreeResources } from 'my-components/MCATLandingPages';
import MCATScorePage from 'my-components/MCATScorePage';
import MCATScoresHub from 'my-components/MCATScoresHub';
import { MCATSectionBalancePage, MCATSectionPage } from 'my-components/MCATSectionPage';
import MedSchoolCalculator from 'my-components/MedSchoolCalculator';
import MessageManager from 'my-components/MessageManager';
import NotesSharingPage from 'my-components/NotesSharingPage';
import NotificationMonitor from 'my-components/NotificationMonitor';
import PartnerDashboard from 'my-components/PartnerDashboard';
import PracticeQuestionPage from 'my-components/PracticeQuestionPage';
import PreMedPlusLandingPage from 'my-components/PreMedPlusLandingPage';
import PreMedTutoringLandingPage from 'my-components/PreMedTutoringLandingPage';
import Pricing from 'my-components/Pricing';
import { PrivacyPolicyDialog, PrivacyPolicyPage } from 'my-components/PrivacyPolicy';
import ProfileForm from 'my-components/ProfileForm';
import SATACTPracticeTestResultsPage from 'my-components/SATACTPracticeTestResultsPage';
import SATDashboard from 'my-components/SATDashboard';
import SATLandingPage from 'my-components/SATLandingPage';
import SATTutoringLandingPage from 'my-components/SATTutoringLandingPage';
import SchoolPage from 'my-components/SchoolPage';
import SignupOnboarding from 'my-components/SignupOnboarding';
import SiteTour from 'my-components/SiteTour';
import StudentCoursePage from 'my-components/StudentCoursePage';
import StudentCoursesDashboard from 'my-components/StudentCoursesDashboard';
import StudentDashboardRedirect from 'my-components/StudentDashboardRedirect';
import { StudentTutoringDashboardPage } from 'my-components/StudentTutoringDashboard';
import StudyPlannerActivityPage from 'my-components/StudyPlannerActivityPage';
import TeacherDashboard from 'my-components/TeacherDashboard';
import { TermsAndConditionsDialog, TermsAndConditionsPage } from 'my-components/TermsAndConditions';
import TextbookContents from 'my-components/TextbookContents';
import TokensDashboard from 'my-components/TokensDashboard';

import AppBar from './AppBar';
import MainErrorBoundary from './MainErrorBoundary';
import PopupChats from './PopupChats';
import SiteWideBanner from './SiteWideBanner';

const AboutUs = Loadable(() => import(/* webpackChunkName: "about-us" */ 'my-components/AboutUs'));
const CoursesPage = Loadable(() => import(/* webpackChunkName: "courses-page" */ 'my-components/CoursesPage'));
const HighSchoolPage = Loadable(
  () => import(/* webpackChunkName: "high-school-landing" */ 'my-components/HighSchoolPage'),
);
const HomePage = Loadable(() => import(/* webpackChunkName: "homepage" */ 'my-components/HomePage'));
const LivePrepLandingPage = Loadable(
  () => import(/* webpackChunkName: "live-prep-landing" */ 'my-components/LivePrepLandingPage'),
);
const OnDemandLandingPage = Loadable(
  () => import(/* webpackChunkName: "on-demand-landing" */ 'my-components/OnDemandLandingPage'),
);
const UniversityPage = Loadable(
  () => import(/* webpackChunkName: "university-landing" */ 'my-components/UniversityPage'),
);

const SearchPage = Loadable(() => import(/* webpackChunkName: "search-page" */ 'my-components/SearchPage'));
const SubscriptionLandingPage = Loadable(
  () => import(/* webpackChunkName: "subscriptions-page" */ 'my-components/SubscriptionLandingPage'),
);

const InstructorsPage = Loadable(() => import(/* webpackChunkName: "instructors" */ 'my-components/InstructorsPage'));

const Blog = Loadable(() => import(/* webpackChunkName: "blog" */ 'my-components/Blog'));
const ContactLinePage = Loadable(
  () => import(/* webpackChunkName: "contact-line" */ 'my-components/ContactLines/ContactLinePage'),
);

const ContentCreatorDashboard = Loadable(
  () => import(/* webpackChunkName: "digitizer-dashboard" */ 'my-components/ContentCreatorDashboard'),
);

const AdminLayout = Loadable(() => import(/* webpackChunkName: "admin-dashboard" */ 'my-components/AdminLayout'));
const Invites = Loadable(() => import(/* webpackChunkName: "invites" */ 'my-components/InvitesPage'));
const EducatorsPage = Loadable(() => import(/* webpackChunkName: "educators" */ 'my-components/EducatorsPage'));
const ParentsPage = Loadable(() => import(/* webpackChunkName: "parents" */ 'my-components/ParentsPage'));

const OnlineCourse = Loadable(() => import(/* webpackChunkName: "online-courses" */ 'my-components/OnlineCourse'));
const OnlineCoursePracticeMode = Loadable(
  () => import(/* webpackChunkName: "online-courses" */ 'my-components/OnlineCoursePracticeMode'),
);
const OnlineCoursePrintToPdf = Loadable(
  () => import(/* webpackChunkName: "online-courses-print" */ 'my-components/OnlineCoursePrintToPdf'),
);
const ShareActivity = Loadable(
  () => import(/* webpackChunkName: "online-courses-share" */ 'my-components/ShareActivity'),
);

/* 'oc-manager' chunk routes */
const OnlineCourseActivitiesManager = Loadable(
  () => import(/* webpackChunkName: "oc-manager" */ 'my-components/OnlineCourseActivitiesManager'),
);
const OnlineCourseManager = Loadable(
  () => import(/* webpackChunkName: "oc-manager" */ 'my-components/OnlineCourseManager'),
);
const OnlineCourseNew = Loadable(() => import(/* webpackChunkName: "oc-manager" */ 'my-components/OnlineCourseNew'));

const StudyInitiative = Loadable(
  () => import(/* webpackChunkName: "study-initiative" */ 'my-components/StudyInitiative'),
);
const StudyRoom = Loadable(() => import(/* webpackChunkName: "study-room" */ 'my-components/StudyRoom'));
const WizeStory = Loadable(() => import(/* webpackChunkName: "wize-story" */ 'my-components/WizeStory'));

const Scholarships = Loadable(() => import(/* webpackChunkName: "scholarships" */ 'my-components/Scholarships'));

const TutorLandingPage = Loadable(() => import(/* webpackChunkName: "tutoring" */ 'my-components/TutorLandingPage'));
const TutorSearch = Loadable(() => import(/* webpackChunkName: "tutor-search" */ 'my-components/TutorSearch'));

const CareersPage = Loadable(() => import(/* webpackChunkName: "job-positions" */ 'my-components/CareersPage'));
const JobPositionPage = Loadable(() => import(/* webpackChunkName: "job-positions" */ 'my-components/JobPositionPage'));

const PartnersPage = Loadable(() => import(/* webpackChunkName: "partnerships" */ 'my-components/PartnersPage'));

const TutorProfile = Loadable(() => import(/* webpackChunkName: "tutor-profile" */ 'my-components/TutorProfile'));
const TeacherReviewPage = Loadable(
  () => import(/* webpackChunkName: "tutor-profile" */ 'my-components/TeacherReviewPage'),
);

export default function MainLayout(props) {
  const { onChangeThemeMode, themeMode } = props;
  const location = useLocation();
  const currentUser = useCurrentUser();

  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [location.pathname]);

  const [userRole, homePath] = useMemo(
    () =>
      currentUser.guest ? ['guest', '/']
      : hasAnyRoles(currentUser, 'admin') ? ['admin', adminDashboardPath()]
      : (
        hasAnyRoles(currentUser, [
          'instructor',
          'ss_instructor',
          'mcat_instructor',
          'mcat_coach',
          'lsat_instructor',
          'lsat_coach',
          'dat_instructor',
          'dat_coach',
          'sat_instructor',
          'sat_coach',
          'act_instructor',
          'act_coach',
        ])
      ) ?
        ['instructor', instructorDashboardPath()]
      : hasAnyRoles(currentUser, 'tutor') ? ['tutor', instructorDashboardPath()]
      : hasAnyRoles(currentUser, 'teacher') ? ['teacher', teacherDashboardPath()]
      : hasAnyRoles(currentUser, 'content_creator') ? ['content_creator', contentCreatorDashboardPath()]
      : hasManagerRole(currentUser) ? ['manager', adminDashboardPath()]
      : ['student', '/'],
    [currentUser],
  );

  if (location.pathname === '/') {
    if (homePath !== '/') {
      return <Navigate replace to={homePath} />;
    } else if (userRole === 'student') {
      return <StudentDashboardRedirect />;
    }
  }

  return (
    <MainErrorBoundary>
      <SiteWideBanner />
      <AppBar homePath={homePath} onChangeThemeMode={onChangeThemeMode} themeMode={themeMode} variant={userRole} />

      {currentUser.guest ?
        <LoginFormDialog />
      : <>
          <ReauthenticateForm />
          <NotificationMonitor />
          <PopupChats />
        </>
      }
      <div style={{ minHeight: '100%', paddingTop: 0 }}>
        {/* eslint-disable perfectionist/sort-jsx-props -- easier to read keeping "path" as first prop */}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/homepage" element={<HomePage />} />
          <Route path="/drm-video-testing" element={<DRMVideoTesting />} />
          <Route path={studentSignupPath()} element={<SignupOnboarding />} />
          <Route path={studentOnboardingPath()} element={<SignupOnboarding />} />
          <Route path={checkoutPath()} element={<SignupOnboarding />} />
          <Route path={studentDashboardPath()} element={<StudentCoursesDashboard />} />
          <Route path={studentMcatDashboardPath()} element={<MCATDashboard />} />
          <Route path={studentLsatDashboardPath()} element={<LSATDashboard />} />
          <Route path={studentDatDashboardPath()} element={<DATDashboard />} />
          <Route path={studentSatDashboardPath()} element={<SATDashboard />} />
          <Route path={studentActDashboardPath()} element={<ACTDashboard />} />
          <Route path={tutoringDashboardPath()} element={<StudentTutoringDashboardPage />} />
          <Route path={adminDashboardPath()}>
            <Route path="*" element={<AdminLayout />} index />
          </Route>
          <Route path={instructorDashboardPath()} element={<InstructorDashboard />} />
          <Route path={contentCreatorDashboardPath()} element={<ContentCreatorDashboard />} />
          <Route path={teacherDashboardPath()}>
            <Route path="*" element={<TeacherDashboard />} index />
          </Route>
          <Route path={aboutPath()} element={<AboutUs />} />
          <Route path={careersPath()} element={<CareersPage />} />
          <Route path={contestPath()} element={<ContestPage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path={highSchoolLandingPath()} element={<HighSchoolPage />} />
          <Route path={universityLandingPath()} element={<UniversityPage />} />
          <Route path="/instructors" element={<InstructorsPage />} />
          <Route path="/scholarships/*" element={<Scholarships />} />
          <Route path="/job-positions/:id" element={<JobPositionPage />} />
          <Route path={mcatEliteLandingPath()} element={<MCAT />} />
          <Route path={mcatFreeResourcesPath()} element={<MCATFreeResources />} />
          <Route path={mcatEventsPath()} element={<FreeEventsLandingPage courseSlug={MCAT_SLUG} />} />
          <Route path={mcatEventPath(':id')} element={<FreeEventLandingPage courseSlug={MCAT_SLUG} />} />
          <Route path={mcatExamDates()} element={<MCATExamDates />} />
          <Route path={mcatScoresPath()} element={<MCATScoresHub />} />
          <Route path={mcatSectionBalancePath()} element={<MCATSectionBalancePage />} />
          {MCAT_SECTION_KEYS.map(key => (
            <Route key={key} path={mcatSectionScorePath(key)} element={<MCATSectionPage slug={key} />} />
          ))}
          <Route path={mcatScorePath(':score')} element={<MCATScorePage />} />
          <Route path={'/cars-passages/:weekSlug'} element={<MCATCARSPassagePage />} />
          <Route path={mcatDailyFlashCardsPath()} element={<MCATDailyFlashCardsPage />} />
          <Route path={mcatExamReportPath(':templateId')} element={<MCATExamReportPage />} />
          <Route path={medSchoolCalculatorPath()} element={<MedSchoolCalculator />} />
          <Route path={lsatLandingPath()} element={<LSATLandingPage />} />
          <Route path={lsatSelfPacedLandingPath()} element={<LSATSelfPacedLandingPage />} />
          <Route path={lsatEventsPath()} element={<FreeEventsLandingPage courseSlug={LSAT_SLUG} />} />
          <Route path={lsatEventPath(':id')} element={<FreeEventLandingPage courseSlug={LSAT_SLUG} />} />
          <Route path={lsatTutoringLandingPath()} element={<LSATTutoringLandingPage />} />
          <Route path={datLandingPath()} element={<DATLandingPage />} />
          <Route path={datEliteLandingPath()} element={<DATEliteLandingPage />} />
          <Route path={datSelfPacedLandingPath()} element={<DATSelfPacedLandingPage />} />
          <Route path={datEventsPath()} element={<FreeEventsLandingPage courseSlug={DAT_SLUG} />} />
          <Route path={datEventPath(':id')} element={<FreeEventLandingPage courseSlug={DAT_SLUG} />} />
          <Route path={datTutoringLandingPath()} element={<DATTutoringLandingPage />} />
          <Route path={satLandingPath()} element={<SATLandingPage />} />
          <Route path={satEventsPath()} element={<FreeEventsLandingPage courseSlug={SAT_SLUG} />} />
          <Route path={satEventPath(':id')} element={<FreeEventLandingPage courseSlug={SAT_SLUG} />} />
          <Route path={satTutoringLandingPath()} element={<SATTutoringLandingPage />} />
          <Route path={actLandingPath()} element={<ACTLandingPage />} />
          <Route path={actEventsPath()} element={<FreeEventsLandingPage courseSlug={ACT_SLUG} />} />
          <Route path={actEventPath(':id')} element={<FreeEventLandingPage courseSlug={ACT_SLUG} />} />
          <Route path={actTutoringLandingPath()} element={<ACTTutoringLandingPage />} />
          <Route path={medSchoolAdmissionsLandingPath()} element={<AdmissionsLandingPage />} />
          <Route path={lsacPracticeTestPath(':testId')} element={<LSACPracticeTestPage />} />
          <Route path={scoresmartTestResultsPath(':testId')} element={<SATACTPracticeTestResultsPage />} />
          <Route path={preMedPlusLandingPath()} element={<PreMedPlusLandingPage />} />
          <Route path={preMedTutoringLandingPath()} element={<PreMedTutoringLandingPage />} />
          <Route path={livePrepLandingPath()} element={<LivePrepLandingPage />} />
          <Route path={onDemandLandingPath()} element={<OnDemandLandingPage />} />
          <Route path={subscriptionLandingPath()} element={<SubscriptionLandingPage />} />
          <Route path={educatorsLandingPath()} element={<EducatorsPage />} />
          <Route path={parentsLandingPath()} element={<ParentsPage />} />
          <Route path="/schools/:id" element={<SchoolPage />} />
          <Route path={livePrepSchoolPath({ slug: ':id' })} element={<SchoolPage />} />
          <Route path={searchPagePath()} element={<SearchPage />} />
          <Route path={invitesPagePath()} element={<Invites />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path={callUsPath()} element={<ContactLinePage line="call" />} />
          <Route path={textUsPath()} element={<ContactLinePage line="text" />} />
          <Route path={partnershipsPath()} element={<PartnersPage />} />
          <Route path="/partners/:idOrSlug" element={<PartnerDashboard />} />
          <Route path={blogPath(':slug?')} element={<Blog />} />
          <Route path={tutoringLandingPath()} element={<TutorLandingPage />} />
          <Route path={tutorSearchPath()} element={<TutorSearch />} />
          <Route path="tutors/:id" element={<TutorProfile />} />
          <Route path="/our-story" element={<WizeStory />} />
          <Route path="/instructors/:id" element={<TutorProfile />} />
          <Route path="/instructors/:id/write-a-review" element={<TeacherReviewPage />} />
          <Route path={`${coursePath(':idOrSlug')}`} element={<CoursePage />} />
          <Route path={courseBookletPath(':idOrSlug')} element={<CourseBookletPage />} />
          <Route path={courseClassRepApplyPath(':idOrSlug')} element={<ClassRepApplicationPage />} />
          <Route path={classRepPositionsPath()} element={<ClassRepPositionsPage />} />
          <Route path={studentCoursePagePath(':idOrSlug')}>
            <Route element={<StudentCoursePage />} path="*" index />
          </Route>
          <Route path={flashCardsPath(':id')} element={<FlashCardsPage />} />
          <Route path={flashCardsEditPath(':id')} element={<FlashCardDeckEditPage />} />
          <Route path="/shares/:token" element={<ShareActivity />} />
          <Route path="/online-courses/new" element={<OnlineCourseNew />} />
          <Route path="/online-courses/:id/manager">
            <Route path="*" element={<OnlineCourseManager />} index />
          </Route>
          <Route path="/online-courses/:id/print">
            <Route path="*" element={<OnlineCoursePrintToPdf />} index />
          </Route>
          <Route path="/online-courses/:id/practice-mode">
            <Route path="*" element={<OnlineCoursePracticeMode />} index />
          </Route>
          <Route path="/online-courses/:id">
            <Route path="*" element={<OnlineCourse />} index />
          </Route>
          <Route path="/online-course-activities">
            <Route path="*" element={<OnlineCourseActivitiesManager />} index />
          </Route>
          <Route path={`${courseAssignmentPath(':id')}/*`} element={<CourseAssignmentPage />} />
          <Route
            path={`${studyPlannerActivityPath(':courseSlug', ':taskId')}/*`}
            element={<StudyPlannerActivityPage />}
          />
          <Route path={studyInitiativePath(':id')} element={<StudyInitiative />} />
          <Route path={aiTutorPath()} element={<LlmChatManager />} />
          <Route path="/study-rooms/:slug" element={<StudyRoom />} />
          <Route path={accountSettingsPath()} element={<ProfileForm />} />
          <Route path="/upload-notes" element={<NotesSharingPage />} />
          <Route path={brandAmbassadorDashboardPath()} element={<MarketingRepDashboard />} />
          <Route path={marketingRepDashboardPath()} element={<MarketingRepDashboard />} />
          <Route path={mcatRepDashboardPath()} element={<MarketingRepDashboard />} />
          <Route path={classRepDashboardPath()} element={<ClassRepDashboard />} />
          <Route path={classRepWizardPath(':crcId')} element={<ClassRepWizardPage />} />
          <Route path={announcementsPath()} element={<Announcements />} />
          <Route path={jobApplicantPath()} element={<JobApplication />} />
          <Route path={chatPagePath('*')} element={<ChatPage />} />
          <Route path={tokensDashboardPath()} element={<TokensDashboard />} />
          <Route path={pricingPath()} element={<Pricing />} />
          <Route path="/textbooks/*" element={<TextbookContents />} />
          <Route path="/practice-questions/:id/*" element={<PracticeQuestionPage />} />
          <Route path={`${essayWriterPath()}/*`} element={<EssayJack />} />
          <Route path={essayWriterLandingPagePath()} element={<EssayJackLandingPage />} />
          <Route path="/lp/:slug" element={<BuilderLandingPage />} />
          <Route path={customProductPath(':slug')} element={<CustomProductCheckoutPage />} />
        </Routes>
        {/* eslint-enable perfectionist/sort-jsx-props */}
        <PrivacyPolicyDialog />
        <TermsAndConditionsDialog />
        <SiteTour />
      </div>
      <MessageManager />
    </MainErrorBoundary>
  );
}
